/**
 * @file 向听数（Shanten）计算 —— `design/gdd/06-ai-strategy.md` §3。
 *
 * - `shantenStandard`：标准型向听数（§3.1，DFS 枚举「面子 + 搭子 + 雀头」划分）；
 * - `shantenQiDui`：七对向听数（§3.2，**四川特殊**：4 张记 2 对 → 龙七对，
 *   与日麻/国标的关键差异是**不能**加 `kinds < 7` 修正项）；
 * - `shantenSichuan`：四川综合向听数（§3.3，缺门牌直接剔除）；
 * - `shantenForMode`：按难度选择「仅标准型 / 完整」的向听（§6.1 向听计算列）。
 *
 * 纯函数、平台无关。入参 `counts` 只读；需要改动的内部一律先拷贝。
 */

import type { Suit } from '../types';
import { suitToIndex } from '../tiles';
import type { ShantenMode } from './params';
import type { TileCounts } from './types';

/** 深拷贝 27 维计数向量。 */
export function copyCounts(counts: TileCounts): Uint8Array {
  return new Uint8Array(counts);
}

/**
 * 标准型向听数（§3.1）。
 *
 * 通用公式：`shanten = 8 - 2*melds - partials - hasPair`，
 * 其中 `melds` 含副露、`partials` 为搭子/对子数（每个最多贡献 1）、
 * `hasPair` 为是否已有雀头。约束：`melds + partials <= 4`（非雀头的面子槽位）。
 *
 * 实现用回溯枚举所有划分：分支为刻子 / 顺子 / 对子（两义：雀头 or 搭子）/
 * 两面搭子 / 坎张搭子 / 孤张跳过。复杂度微秒级（27 位 × 6 分支 + 剪枝）。
 *
 * @param counts    手牌计数向量（**不要求**已剔除缺门；剔除由 `shantenSichuan` 负责）
 * @param meldCount 已副露面子数（碰 / 杠各 1）
 */
export function shantenStandard(counts: TileCounts, meldCount: number): number {
  const c = copyCounts(counts);
  let best = 8;

  function dfs(i: number, melds: number, partials: number, hasPair: boolean): void {
    if (best <= -1) return; // 已是最优（-1 = 已胡），不必再搜
    if (melds + partials > 5) return; // 剪枝：总组数不可能超过 5
    if (i >= 27) {
      const m = meldCount + melds;
      // 非雀头的面子+搭子最多 4 组；m >= 4 时搭子不再计分
      const p = Math.max(0, Math.min(partials, 4 - m));
      let s = 8 - 2 * m - p - (hasPair ? 1 : 0);
      // §3.1 的防御性修正：凑满 5 组但无雀头时 +1。
      // 注：p 已封顶于 4 - m，故 m + p <= 4 恒成立，此分支实际不会触发；
      // 保留它仅为忠实复刻文档公式，防止将来改封顶策略时漏掉该情形。
      if (m + p === 5 && !hasPair) s += 1;
      if (s < best) best = s;
      return;
    }

    const cnt = c[i] ?? 0;
    if (cnt === 0) {
      dfs(i + 1, melds, partials, hasPair);
      return;
    }

    // 分支 1：刻子
    if (cnt >= 3) {
      c[i] = cnt - 3;
      dfs(i, melds + 1, partials, hasPair);
      c[i] = cnt;
    }
    // 分支 2：顺子（★ 花色边界 i%9 <= 6，与 03 已验证的边界一致）
    if (i % 9 <= 6 && (c[i + 1] ?? 0) > 0 && (c[i + 2] ?? 0) > 0) {
      c[i] = cnt - 1;
      c[i + 1] = (c[i + 1] ?? 0) - 1;
      c[i + 2] = (c[i + 2] ?? 0) - 1;
      dfs(i, melds + 1, partials, hasPair);
      c[i] = cnt;
      c[i + 1] = (c[i + 1] ?? 0) + 1;
      c[i + 2] = (c[i + 2] ?? 0) + 1;
    }
    // 分支 3：对子 —— 两义
    if (cnt >= 2) {
      c[i] = cnt - 2;
      dfs(i, melds, partials, true); // 3a：作雀头（hasPair=true，不计入搭子槽）
      dfs(i, melds, partials + 1, hasPair); // 3b：作搭子（可再摸一张成刻）
      c[i] = cnt;
    }
    // 分支 4：两面/边张搭子
    if (i % 9 <= 7 && (c[i + 1] ?? 0) > 0) {
      c[i] = cnt - 1;
      c[i + 1] = (c[i + 1] ?? 0) - 1;
      dfs(i, melds, partials + 1, hasPair);
      c[i] = cnt;
      c[i + 1] = (c[i + 1] ?? 0) + 1;
    }
    // 分支 5：坎张搭子
    if (i % 9 <= 6 && (c[i + 2] ?? 0) > 0) {
      c[i] = cnt - 1;
      c[i + 2] = (c[i + 2] ?? 0) - 1;
      dfs(i, melds, partials + 1, hasPair);
      c[i] = cnt;
      c[i + 2] = (c[i + 2] ?? 0) + 1;
    }
    // 分支 6：孤张，跳过（不参与任何结构，公式会把它算作「还需摸」）
    dfs(i + 1, melds, partials, hasPair);
  }

  dfs(0, 0, 0, false);
  return best;
}

/**
 * 七对向听数（§3.2）。
 *
 * ★ 四川麻将特殊点：**4 张记 2 对（龙七对）**，因此**不能**加日麻/国标的
 * `if (kinds < 7) shanten += 7 - kinds` 修正项。漏删这一项会让 AI 严重低估
 * 龙七对牌型的价值 —— 这是很隐蔽的移植 bug。
 *
 * @returns 有副露（`meldCount > 0`）时七对不可能，返回大数 100（可序列化、可比较的 INFINITY）
 */
export function shantenQiDui(counts: TileCounts, meldCount: number): number {
  if (meldCount > 0) return 100;
  let pairs = 0;
  for (let k = 0; k < 27; k++) pairs += Math.floor((counts[k] ?? 0) / 2);
  return 6 - pairs;
}

/**
 * 四川麻将综合向听数（§3.3）。
 *
 * Step 1：缺门牌是绝对废牌，直接剔除（剔除后手牌张数不足是正常的 ——
 * 向听公式不依赖总张数，少掉的张数天然表现为「还需要摸这么多有效牌」）。
 * Step 2：取标准型与七对的最小值。
 *
 * @param counts    手牌计数向量
 * @param meldCount 已副露面子数
 * @param queSuit   定缺花色；`null` 表示尚未定缺（不剔除任何花色）
 */
export function shantenSichuan(
  counts: TileCounts,
  meldCount: number,
  queSuit: Suit | null,
): number {
  return shantenForMode(counts, meldCount, queSuit, 'FULL');
}

/**
 * 按难度选择向听口径（§6.1 向听计算列）：
 * `STANDARD_ONLY` 仅标准型（简单档）；`FULL` 标准型 + 七对（普通/困难档）。
 * 无论哪档，缺门牌都先剔除（缺门牌无法参与任何最终结构）。
 */
export function shantenForMode(
  counts: TileCounts,
  meldCount: number,
  queSuit: Suit | null,
  mode: ShantenMode,
): number {
  const c = copyCounts(counts);
  if (queSuit !== null) {
    const base = suitToIndex(queSuit) * 9;
    for (let r = 0; r < 9; r++) c[base + r] = 0;
  }
  const std = shantenStandard(c, meldCount);
  if (mode === 'STANDARD_ONLY') return std;
  return Math.min(std, shantenQiDui(c, meldCount));
}
